import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { fetchPosts, fetchThemePosts } from '../api'
import { colors } from '../colors'
import {
  BackButton,
  InfinitePostList,
  LoginModal,
  PageContainer,
  PageHeader,
  UserIcon,
} from '../components'
import PostReactions from '../components/posts/PostReactions'
import officialMark from '../images/official_mark.png'
import type { BargainUserInfo, Chain, PaginationParams, Post } from '../types'

interface PostShowProps {
  post: Post
  bargain_user_info: BargainUserInfo
  random_seed: number
  followed_chains?: Chain[]
}

const styles: Record<string, React.CSSProperties> = {
  postImages: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    margin: '4px 14px 12px',
  },
  postImagesThree: {
    height: '280px',
    display: 'grid',
    gridTemplateColumns: '60% 40%',
    gridTemplateRows: '1fr 1fr',
    gap: '4px',
  },
  postImage: {
    overflow: 'hidden',
  },
  postImageOne: {
    height: 'auto',
    margin: '0 auto',
  },
  postImageTwo: {
    width: 'calc(50% - 2px)',
    aspectRatio: '1',
  },
  postImageThreeFirst: {
    gridColumn: '1',
    gridRow: '1 / 3',
    height: '100%',
  },
  postImageThreeSecond: {
    gridColumn: '2',
    gridRow: '1',
    height: '100%',
  },
  postImageThreeThird: {
    gridColumn: '2',
    gridRow: '2',
    height: '100%',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    cursor: 'pointer',
  },
  imageOne: {
    width: '100%',
    height: 'auto',
    maxHeight: '400px',
    objectFit: 'contain',
    objectPosition: 'left',
    display: 'block',
    cursor: 'pointer',
  },
  content: {
    padding: '0 14px',
  },
  titlePriceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    gap: '8px',
  },
  productName: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: colors['text-default'],
  },
  postText: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: colors['text-default'],
    marginBottom: '8px',
    whiteSpace: 'pre-wrap',
  },
  themeTag: {
    fontSize: '14px',
    fontWeight: '600',
    color: colors['keijiban-coral'],
    marginBottom: '8px',
    textDecoration: 'none',
    display: 'inline-block',
  },
  shopInfo: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    textDecoration: 'none',
    color: 'inherit',
  },
  logoWrapper: {
    width: '36px',
    height: '36px',
    marginRight: '4px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  shopName: {
    fontSize: '14px',
    color: '#000000DE',
  },
  userInfoSection: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
    fontSize: '12px',
    color: colors['text-secondary'],
    gap: '8px',
  },
  userNameWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  officialMark: {
    width: '14px',
    height: '14px',
    marginLeft: '4px',
  },
  priceWrapper: {
    display: 'flex',
    alignItems: 'baseline',
    flexShrink: 0,
  },
  price: {
    fontSize: '14px',
    color: colors['text-secondary'],
  },
  tax: {
    fontSize: '12px',
    color: colors['text-secondary'],
  },
  priceDisclaimer: {
    fontSize: '10px',
    color: '#0000008A',
    backgroundColor: '#0000000A',
    marginBottom: '8px',
    lineHeight: '1.4',
    padding: '8px',
    borderRadius: '4px',
  },
  userName: {
    color: colors['text-secondary'],
  },
  // モーダル用スタイル
  fullscreenOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    cursor: 'pointer',
  },
  modalContainer: {
    position: 'relative',
    maxWidth: '100vw',
    maxHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageSlider: {
    display: 'flex',
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    width: '100%',
    height: '100%',
    gap: '20px',
    padding: '0',
  },
  slideImage: {
    width: '100vw',
    maxHeight: '100%',
    objectFit: 'contain',
    scrollSnapAlign: 'center',
    flexShrink: 0,
  },
  closeButton: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    fontSize: '40px',
    color: 'white',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  relatedPostsSection: {
    marginTop: '24px',
  },
  relatedPostsTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: colors['text-default'],
    marginBottom: '16px',
    padding: '0 15px',
  },
  relatedPostsLoading: {
    textAlign: 'center',
    padding: '20px',
    color: colors['text-secondary'],
    fontSize: '14px',
  },
  footerButtons: {
    boxSizing: 'border-box',
    maxWidth: '640px',
    width: '100%',
    position: 'fixed',
    bottom: '16px',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: '8px 14px',
    gap: '8px',
    zIndex: 10,
  },
}

const PC_BREAKPOINT = 640

const PostShow = ({
  post,
  bargain_user_info,
  random_seed,
  followed_chains = [],
}: PostShowProps) => {
  const [isPC, setIsPC] = useState(window.innerWidth >= PC_BREAKPOINT)
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const imageSliderRef = useRef<HTMLDivElement>(null)

  // フィルター条件を維持した投稿一覧
  const [relatedPosts, setRelatedPosts] = useState<Post[]>([])
  const [isLoadingRelatedPosts, setIsLoadingRelatedPosts] = useState(true)

  // URLパラメータからフィルタータイプを取得
  const { urlFilterType, urlChainId, urlThemeId } = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    return {
      urlFilterType: params.get('filter_type'),
      urlChainId: params.get('chain_id'),
      urlThemeId: params.get('theme_id'),
    }
  }, [])

  // フィルタータイプに基づくセクションタイトルを取得
  const sectionTitle = useMemo(() => {
    // テーマからの遷移の場合、URLのtheme_idと投稿のtheme.idが一致することを確認
    if (urlThemeId && post.theme && String(post.theme.id) === urlThemeId) {
      return `「${post.theme.title}」の口コミ`
    }
    if (urlFilterType === 'chain' && urlChainId) {
      const chain = followed_chains.find((c) => String(c.id) === urlChainId)
      return chain ? `${chain.name}の口コミ` : 'チェーンの口コミ'
    }
    if (urlFilterType === 'following') {
      return 'フォロー中のチェーンの口コミ'
    }
    if (urlFilterType === 'want_to_eat') {
      return '「食べたい！」を押した口コミ'
    }
    return 'すべての口コミ'
  }, [urlFilterType, urlChainId, urlThemeId, followed_chains, post.theme])

  // フィルター条件に基づいて投稿を取得
  useEffect(() => {
    const loadRelatedPosts = async () => {
      setIsLoadingRelatedPosts(true)
      try {
        const params: PaginationParams = {
          page: 1,
          offset: 0,
          random_seed,
        }

        let response
        if (urlThemeId) {
          // テーマからの遷移の場合はテーマの投稿を取得
          response = await fetchThemePosts(Number(urlThemeId), params)
        } else {
          if (urlFilterType === 'chain' && urlChainId) {
            params.filter_type = 'chain'
            params.chain_id = urlChainId
          } else if (urlFilterType === 'following') {
            params.filter_type = 'following'
          } else if (urlFilterType === 'want_to_eat') {
            params.filter_type = 'want_to_eat'
          }
          response = await fetchPosts(params)
        }

        // 現在表示中の投稿を除外
        const filteredPosts = response.posts.filter((p) => p.id !== post.id)
        setRelatedPosts(filteredPosts)
      } catch (error) {
        console.error('Failed to fetch related posts:', error)
        setRelatedPosts([])
      } finally {
        setIsLoadingRelatedPosts(false)
      }
    }

    void loadRelatedPosts()
  }, [urlFilterType, urlChainId, urlThemeId, random_seed, post.id])

  // 無限スクロール用の追加データ取得
  const fetchMoreRelatedPosts = useCallback(
    async (page: number, offset: number) => {
      const params: PaginationParams = {
        page,
        offset,
        random_seed,
      }

      let response
      if (urlThemeId) {
        // テーマからの遷移の場合はテーマの投稿を取得
        response = await fetchThemePosts(Number(urlThemeId), params)
      } else {
        if (urlFilterType === 'chain' && urlChainId) {
          params.filter_type = 'chain'
          params.chain_id = urlChainId
        } else if (urlFilterType === 'following') {
          params.filter_type = 'following'
        } else if (urlFilterType === 'want_to_eat') {
          params.filter_type = 'want_to_eat'
        }
        response = await fetchPosts(params)
      }

      // 現在表示中の投稿を除外
      const filteredPosts = response.posts.filter((p) => p.id !== post.id)

      return { posts: filteredPosts }
    },
    [random_seed, urlFilterType, urlChainId, urlThemeId, post.id]
  )

  useEffect(() => {
    const handleResize = () => {
      setIsPC(window.innerWidth >= PC_BREAKPOINT)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // モーダルが開いたときに選択された画像にスクロール
  useEffect(() => {
    if (selectedImageIndex !== null && imageSliderRef.current) {
      const slider = imageSliderRef.current
      const imageWidth = slider.scrollWidth / (post.images?.length || 1)
      const scrollPosition = selectedImageIndex * imageWidth

      slider.scrollLeft = scrollPosition
    }
  }, [selectedImageIndex, post.images?.length])

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index)
  }

  const handleCloseModal = () => {
    setSelectedImageIndex(null)
  }

  const hasImages = post.images && post.images.length > 0
  const hasPurchasedPrice = post.purchased_price > 0

  const posterName = post.poster_info?.name ?? post.poster_name
  const isOfficialPost = post.poster_info?.is_tokubai_staff_post ?? false

  return (
    <PageContainer maxWidth="640px">
      <PageHeader title={sectionTitle} bargain_user_info={bargain_user_info} />

      {hasImages && (
        <div
          style={{
            ...styles.postImages,
            ...(post.images.length >= 3 ? styles.postImagesThree : {}),
          }}
        >
          {post.images.map((image, idx) => (
            <div
              key={image.id}
              style={{
                ...styles.postImage,
                ...(post.images.length === 1 ? styles.postImageOne : {}),
                ...(post.images.length === 2 ? styles.postImageTwo : {}),
                ...(post.images.length === 3 && idx === 0 ? styles.postImageThreeFirst : {}),
                ...(post.images.length === 3 && idx === 1 ? styles.postImageThreeSecond : {}),
                ...(post.images.length === 3 && idx === 2 ? styles.postImageThreeThird : {}),
              }}
            >
              <img
                src={image.image_url}
                alt={post.product_name}
                style={post.images.length === 1 ? styles.imageOne : styles.image}
                onClick={() => handleImageClick(idx)}
              />
            </div>
          ))}
        </div>
      )}

      <div style={styles.content}>
        <div style={styles.titlePriceRow}>
          <div style={styles.productName}>{post.product_name}</div>
          {hasPurchasedPrice && (
            <div style={styles.priceWrapper}>
              <span style={styles.price}>{post.purchased_price.toLocaleString()}円</span>
              <span style={styles.tax}>(税込)</span>
            </div>
          )}
        </div>

        {post.content && <div style={styles.postText}>{post.content}</div>}
        {post.theme && (
          <a href={`/webview/message_board/themes/${post.theme.id}/posts`} style={styles.themeTag}>
            #{post.theme.title}
          </a>
        )}

        <div style={styles.userInfoSection}>
          <div style={styles.userNameWrapper}>
            <UserIcon />
            <span style={styles.userName}>{posterName}</span>
            {isOfficialPost && (
              <img style={styles.officialMark} src={officialMark} alt="official_mark" />
            )}
          </div>
          <span style={styles.postDate}>
            {new Date(post.created_at).toLocaleDateString('ja-JP', {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
            })}
          </span>
        </div>

        {post.shop && (
          <a href={`/${post.shop.id}`} style={styles.shopInfo}>
            {post.shop.logo_url && (
              <div style={styles.logoWrapper}>
                <img src={post.shop.logo_url} style={styles.logo} alt={post.shop.chain_name} />
              </div>
            )}
            <div style={styles.shopName}>
              {post.shop.chain_name}
              {post.shop.name && ` ${post.shop.name}`}
            </div>
          </a>
        )}

        <div style={styles.priceDisclaimer}>
          ※商品名・価格は投稿者の申告情報です。実際と異なる場合がありますので目安としてご覧ください。
        </div>

        <PostReactions post={post} bargain_user_info={bargain_user_info} />
      </div>

      <div style={styles.relatedPostsSection}>
        <div style={styles.relatedPostsTitle}>{sectionTitle}</div>
        {isLoadingRelatedPosts ? (
          <div style={styles.relatedPostsLoading}>読み込み中...</div>
        ) : (
          <InfinitePostList
            initialPosts={relatedPosts}
            bargain_user_info={bargain_user_info}
            fetchMorePosts={fetchMoreRelatedPosts}
            emptyMessage="該当する口コミはありません。"
          />
        )}
      </div>

      <div style={styles.footerButtons}>
        <BackButton href="/webview/message_board/posts">
          <span className="icon_font icf_arrow_left" style={{ fontSize: '18px' }} />
          すべての口コミ
        </BackButton>

        <BackButton
          href={bargain_user_info.type !== 'guest' ? '/webview/message_board/posts/new' : undefined}
          variant="primary"
          onClick={bargain_user_info.type === 'guest' ? () => setShowLoginModal(true) : undefined}
        >
          <span className="icon_font icf_camera" style={{ fontSize: '18px' }} />
          投稿する
        </BackButton>
      </div>

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />

      {selectedImageIndex !== null && post.images && (
        <div style={styles.fullscreenOverlay} onClick={handleCloseModal}>
          <div
            style={{
              ...styles.modalContainer,
              ...(isPC ? { maxWidth: '900px' } : {}),
            }}
          >
            <div ref={imageSliderRef} style={styles.imageSlider}>
              {post.images.map((image) => (
                <img
                  key={image.id}
                  src={image.image_url}
                  alt={post.product_name}
                  style={{
                    ...styles.slideImage,
                    ...(isPC ? { width: '900px' } : {}),
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              ))}
            </div>
            <button
              type="button"
              style={styles.closeButton}
              onClick={(e) => {
                e.stopPropagation()
                handleCloseModal()
              }}
              aria-label="モーダルを閉じる"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </PageContainer>
  )
}

export default PostShow
